import { forwardRef, useState, useEffect } from "react"
import { Input } from "./input"
import { Label } from "./label"
import { cn } from "@/lib/utils"
import { validateRatingInput, validateDecisionDate, validateReviewIntervals, validateDueDateCalculation } from "@/lib/validations"
import { AlertTriangle, CheckCircle, Calendar } from "lucide-react"

interface ValidatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  description?: string
  validationType?: 'rating' | 'date' | 'review-interval'
  onValidationChange?: (isValid: boolean, error?: string, warning?: string) => void
  showInlineValidation?: boolean
}

export const ValidatedInput = forwardRef<HTMLInputElement, ValidatedInputProps>(
  ({ className, label, description, validationType, onValidationChange, showInlineValidation = true, onChange, ...props }, ref) => {
    const [error, setError] = useState<string>()
    const [warning, setWarning] = useState<string>()
    const [isValid, setIsValid] = useState(true)

    const validateInput = (value: string) => {
      if (!validationType) return

      let validationResult: { isValid: boolean; error?: string; warning?: string } = { isValid: true }

      switch (validationType) {
        case 'rating':
          validationResult = validateRatingInput(value)
          break
        case 'date':
          validationResult = validateDecisionDate(value)
          break
        case 'review-interval': {
          const numValue = parseInt(value)
          if (isNaN(numValue)) {
            validationResult = { isValid: false, error: 'Please enter a valid number' }
          } else {
            const intervalResult = validateReviewIntervals([numValue])
            validationResult = {
              isValid: intervalResult.isValid,
              error: intervalResult.errors[0],
              warning: intervalResult.warnings[0]
            }
          }
          break
        }
      }

      setIsValid(validationResult.isValid)
      setError(validationResult.error)
      setWarning(validationResult.warning)

      onValidationChange?.(validationResult.isValid, validationResult.error, validationResult.warning)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      validateInput(value)
      onChange?.(e)
    }

    // Determine input styling based on validation state
    const inputClassName = cn(
      className,
      !isValid && "border-destructive focus:border-destructive",
      warning && isValid && "border-yellow-500 focus:border-yellow-500",
      isValid && !warning && props.value && "border-green-500"
    )

    const getIcon = () => {
      if (!isValid) return <AlertTriangle className="h-4 w-4 text-destructive" />
      if (warning) return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      if (isValid && props.value && validationType) return <CheckCircle className="h-4 w-4 text-green-500" />
      if (validationType === 'date') return <Calendar className="h-4 w-4 text-muted-foreground" />
      return null
    }

    return (
      <div className="space-y-2">
        {label && <Label htmlFor={props.id}>{label}</Label>}
        <div className="relative">
          <Input
            ref={ref}
            className={inputClassName}
            onChange={handleChange}
            {...props}
          />
          {showInlineValidation && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {getIcon()}
            </div>
          )}
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {showInlineValidation && error && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {error}
          </p>
        )}
        {showInlineValidation && warning && isValid && (
          <p className="text-sm text-yellow-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {warning}
          </p>
        )}
      </div>
    )
  }
)

ValidatedInput.displayName = "ValidatedInput"

interface ValidatedRatingSliderProps {
  value: number[]
  onValueChange: (value: number[]) => void
  min?: number
  max?: number
  step?: number
  label?: string
  showValidation?: boolean
  className?: string
}

export function ValidatedRatingSlider({
  value,
  onValueChange,
  min = 1,
  max = 10,
  step = 1,
  label,
  showValidation = true,
  className
}: ValidatedRatingSliderProps) {
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (showValidation && value[0] !== undefined) {
      const validation = validateRatingInput(value[0])
      setError(validation.error)
    }
  }, [value, showValidation])

  return (
    <div className={cn("space-y-3", className)}>
      {label && <Label>{label}</Label>}
      <div className="space-y-2">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium">Rating:</span>
          <span className="text-xl font-bold">{value[0]}/10</span>
          {showValidation && !error && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
          {showValidation && error && (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          )}
        </div>
        <div className="relative">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value[0]}
            onChange={(e) => onValueChange([parseInt(e.target.value)])}
            className={cn(
              "w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer",
              "slider-thumb:appearance-none slider-thumb:h-4 slider-thumb:w-4 slider-thumb:rounded-full",
              "slider-thumb:bg-primary slider-thumb:cursor-pointer",
              error && "bg-red-200"
            )}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{min} - Not confident</span>
          <span>{max} - Very confident</span>
        </div>
        {showValidation && error && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

interface ReviewIntervalValidatorProps {
  intervals: number[]
  decisionDate?: string
  onValidationChange?: (result: { isValid: boolean; errors: string[]; warnings: string[] }) => void
  showDueDates?: boolean
}

export function ReviewIntervalValidator({
  intervals,
  decisionDate,
  onValidationChange,
  showDueDates = true
}: ReviewIntervalValidatorProps) {
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean
    errors: string[]
    warnings: string[]
    dueDates?: Array<{ day: number; date: string; dayOfWeek: string }>
  }>({ isValid: true, errors: [], warnings: [], dueDates: [] })

  useEffect(() => {
    const intervalValidation = validateReviewIntervals(intervals)
    
    let dueDateValidation: { isValid: boolean; errors: string[]; warnings: string[]; dueDates: Array<{ day: number; date: string; dayOfWeek: string }> } = { isValid: true, errors: [], warnings: [], dueDates: [] }
    if (decisionDate && intervalValidation.isValid) {
      dueDateValidation = validateDueDateCalculation(decisionDate, intervals)
    }

    const result = {
      isValid: intervalValidation.isValid && dueDateValidation.isValid,
      errors: [...intervalValidation.errors, ...dueDateValidation.errors],
      warnings: [...intervalValidation.warnings, ...dueDateValidation.warnings],
      dueDates: dueDateValidation.dueDates
    }

    setValidationResult(result)
    onValidationChange?.(result)
  }, [intervals, decisionDate, onValidationChange])

  if (!validationResult.errors.length && !validationResult.warnings.length && !showDueDates) {
    return null
  }

  return (
    <div className="space-y-2">
      {validationResult.errors.map((error, index) => (
        <p key={index} className="text-sm text-destructive flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {error}
        </p>
      ))}
      
      {validationResult.warnings.map((warning, index) => (
        <p key={index} className="text-sm text-yellow-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {warning}
        </p>
      ))}

      {showDueDates && validationResult.dueDates && validationResult.dueDates.length > 0 && (
        <div className="p-3 bg-muted rounded-lg">
          <h4 className="text-sm font-medium mb-2">Scheduled Review Dates:</h4>
          <div className="space-y-1">
            {validationResult.dueDates.map((dueDate) => (
              <div key={dueDate.day} className="flex justify-between text-sm">
                <span>Day {dueDate.day}:</span>
                <span>{dueDate.dayOfWeek}, {dueDate.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}